import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../utils/cn';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
}: ConfirmDialogProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>
        {/* `overflow-y-auto` on the outer flex so very long confirmation
            messages (e.g. multi-line deletion warnings) scroll instead
            of clipping off the bottom on short viewports. */}
        <div className="fixed inset-0 flex items-start justify-center overflow-y-auto p-4 sm:items-center">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="my-auto max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-xl border border-border bg-surface-card text-content-primary p-6 shadow-xl">
              <div className="flex items-start gap-3">
                <div className={cn('flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0',
                  variant === 'danger' ? 'bg-danger-light text-danger' : variant === 'warning' ? 'bg-warning-light text-warning' : 'bg-info-light text-info'
                )}>
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <Dialog.Title className="text-base font-semibold text-content-primary">{title}</Dialog.Title>
                  <p className="mt-1 text-sm text-content-secondary">{message}</p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-content-secondary hover:bg-surface-card-hover transition-colors">
                  {cancelLabel}
                </button>
                <button
                  onClick={() => { onConfirm(); onClose(); }}
                  className={cn('rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors',
                    variant === 'danger' ? 'bg-danger hover:bg-danger/90' : variant === 'warning' ? 'bg-warning hover:bg-warning/90' : 'bg-primary hover:bg-primary-hover'
                  )}
                >
                  {confirmLabel}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
